import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function CustomerPurchaseRequestResponse() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const presetAction = params.get("action"); // accept|decline|null

  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/purchase-requests/${id}/customer`, { params: { token } })
      .then((r) => setPr(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Reservation not found or link expired"))
      .finally(() => setLoading(false));
  }, [id, token]);

  const respond = async (action) => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/purchase-requests/${id}/customer-respond`, { token, action });
      setResult(r.data.result);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit response");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Layout><div className="max-w-2xl mx-auto px-6 py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div></Layout>;
  if (error && !pr) return <Layout><div className="max-w-2xl mx-auto px-6 py-20 text-center"><XCircle size={48} className="mx-auto mb-4" style={{ color: "var(--ab-danger)" }} /><p>{error}</p></div></Layout>;
  if (!pr) return null;

  if (result === "accepted") {
    return <Layout>
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="customer-pr-accepted">
        <CheckCircle2 size={56} className="mx-auto mb-6" style={{ color: "var(--ab-primary)" }} />
        <h1 className="font-display text-3xl font-black mb-3">Thanks!</h1>
        <p style={{ color: "var(--ab-text-muted)" }}>We&apos;ll finalise your reservation and email you the pickup window shortly.</p>
        <div className="mt-8"><Link to="/" className="btn-primary">Home</Link></div>
      </div>
    </Layout>;
  }
  if (result === "deleted") {
    return <Layout>
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="customer-pr-deleted">
        <XCircle size={56} className="mx-auto mb-6" style={{ color: "var(--ab-danger)" }} />
        <h1 className="font-display text-3xl font-black mb-3">Reservation cancelled.</h1>
        <p style={{ color: "var(--ab-text-muted)" }}>No problem — we&apos;ve released the items back to inventory.</p>
        <div className="mt-8"><Link to="/inventory-lookup" className="btn-primary">Back to Shop</Link></div>
      </div>
    </Layout>;
  }

  const items = pr.pending_edits?.items || pr.items;
  const total = pr.pending_edits?.total ?? pr.total;
  const noEdit = pr.status !== "pending_customer_review";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Your Reservation</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mb-6">{pr.customer_name}</h1>
        {pr.pending_edits?.note && (
          <div className="surface p-4 mb-6" style={{ borderLeft: "3px solid var(--ab-warning)" }} data-testid="customer-pr-note">
            <div className="label-eyebrow mb-1" style={{ color: "var(--ab-warning)" }}>Note from us</div>
            <p className="text-sm" style={{ color: "var(--ab-text)" }}>{pr.pending_edits.note}</p>
          </div>
        )}
        <div className="surface divide-y mb-6" style={{ borderColor: "var(--ab-border)" }}>
          {items.map((it, i) => (
            <div key={i} className="flex justify-between items-center px-5 py-4" data-testid="customer-pr-row">
              <div className="min-w-0 flex-1">
                <div className="font-bold">{it.title}</div>
                <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{it.console} · {it.components || ""} · {it.condition || ""}</div>
              </div>
              <div className="font-mono font-bold" style={{ color: "var(--ab-primary)" }}>${Number(it.price).toFixed(it.is_non_game ? 2 : 0)}</div>
            </div>
          ))}
          <div className="flex justify-between items-center px-5 py-4">
            <span className="label-eyebrow">Total</span>
            <span className="font-mono font-bold text-2xl" style={{ color: "var(--ab-primary)" }} data-testid="customer-pr-total">${Number(total).toFixed(2)}</span>
          </div>
        </div>

        {noEdit ? (
          <div className="surface p-5 text-sm" style={{ color: "var(--ab-text-muted)" }} data-testid="customer-pr-no-action">
            This reservation is currently <b>{pr.status.replaceAll("_", " ")}</b>. No action needed from you right now — we&apos;ll email if anything changes.
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 items-center">
            <button onClick={() => respond("accept")} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="customer-pr-accept">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Accept Updated List
            </button>
            <button onClick={() => respond("decline")} disabled={busy} className="btn-danger inline-flex items-center gap-2" data-testid="customer-pr-decline">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Cancel Reservation
            </button>
            {presetAction && <span className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{presetAction === "accept" ? "Click Accept to confirm." : "Click Cancel to decline."}</span>}
          </div>
        )}
        {error && <div className="mt-4 text-sm" style={{ color: "var(--ab-danger)" }}>{error}</div>}
      </div>
    </Layout>
  );
}
